import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import api from '../../../config/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
`;

const Title = styled.h3`
  margin: 0 0 1rem 0;
  color: ${theme.colors.text.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  background: ${props => props.primary ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.primary ? 'white' : theme.colors.text.primary};
`;

const ProfileSettings = () => {
  const { user, updateProfile } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [useDemo, setUseDemo] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProfile = async () => {
    try {
      const me = await api.get('/api/auth/me');
      const u = me.data.user || {};
      setFirstName(u.first_name || '');
      setLastName(u.last_name || '');
      setEmail(u.email || '');
      setPhone(u.phone || '');
      setUseDemo(!!u.use_demo_data);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile data');
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Update form when user context changes
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setUseDemo(!!user.use_demo_data);
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const result = await updateProfile({
        first_name: firstName,
        last_name: lastName,
        phone,
        use_demo_data: useDemo,
      });

      if (result.success) {
        await loadProfile();
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Title>My Account</Title>
      <Grid>
        <div>
          <Label>First Name</Label>
          <Input value={firstName} onChange={e => setFirstName(e.target.value)} />
        </div>
        <div>
          <Label>Last Name</Label>
          <Input value={lastName} onChange={e => setLastName(e.target.value)} />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={email} disabled />
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 555-5555" />
        </div>
        <div>
          <Row>
            <input id="demo-toggle" type="checkbox" checked={useDemo} onChange={e => setUseDemo(e.target.checked)} />
            <Label htmlFor="demo-toggle" style={{ margin: 0 }}>Use demo data (helpful for first-time setup)</Label>
          </Row>
        </div>
      </Grid>
      <ButtonRow>
        <Button onClick={save} primary disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
      </ButtonRow>
    </Card>
  );
};

export default ProfileSettings;
